import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function BellvillePage() {
  useEffect(() => {
    document.title = "Bellville Painters Cape Town | Professional Painting Services | Residential & Commercial Experts";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional painting services for Bellville properties. Expert interior & exterior painting for homes and businesses in Bellville, Northern Suburbs, Cape Town.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Bellville painters, Northern Suburbs painting, commercial painting, Bellville businesses, Cape Town painters');
    }
    
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bellville`);
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Bellville Painters - Professional Painting Services",
      "description": "Professional painting services for Bellville properties with quality finishes and expert craftsmanship.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bellville`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Bellville",
        "description": "Professional painting services for Bellville residential and commercial properties.",
        "areaServed": "Bellville, Cape Town",
        "serviceType": "Professional Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Bellville')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Residential Painting',
      description: 'Complete interior and exterior painting for Bellville homes. Quality finishes for established neighborhoods and new developments throughout the area.',
      price: 'From R8,000',
      features: ['Interior painting', 'Exterior painting', 'Color consultation', 'Quality materials', 'Professional finish']
    },
    {
      icon: 'ri-building-line',
      title: 'Commercial Painting',
      description: 'Professional painting for Bellville\'s thriving business district. Offices, retail spaces, and commercial complexes with flexible scheduling.',
      price: 'From R12,000',
      features: ['Office buildings', 'Retail centers', 'Industrial units', 'After-hours service', 'Minimal disruption']
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Specialized Services',
      description: 'Expert roof painting, waterproofing, and protective coatings for Bellville properties. Comprehensive protection solutions.',
      price: 'From R6,000',
      features: ['Roof painting', 'Waterproofing', 'Protective coatings', 'Weather resistance', 'Long-term durability']
    }
  ];

  const features = [
    {
      title: 'Commercial Expertise',
      description: 'Extensive experience with Bellville\'s commercial properties. We understand business needs and deliver efficient, professional service with minimal disruption.'
    },
    {
      title: 'Quality Materials',
      description: 'Premium paints from leading brands including Dulux and Plascon. Ensuring durable, beautiful finishes suitable for Bellville\'s climate and property types.'
    },
    {
      title: 'Flexible Scheduling',
      description: 'Adaptable work schedules to suit residential and commercial clients. After-hours and weekend services available for business properties.'
    },
    {
      title: 'Professional Team',
      description: 'Experienced craftsmen delivering consistent quality. Clean work practices, punctual service, and respect for your property and business operations.'
    }
  ];

  const paintingProcess = [
    {
      step: 1,
      title: 'Free Consultation',
      description: 'Comprehensive assessment of your Bellville property with detailed quotation. We discuss your requirements and provide expert recommendations.'
    },
    {
      step: 2,
      title: 'Surface Preparation',
      description: 'Thorough preparation including cleaning, repairs, and priming. Proper preparation ensures optimal adhesion and long-lasting results.'
    },
    {
      step: 3,
      title: 'Expert Application',
      description: 'Professional painting using quality materials and proven techniques. Our skilled team ensures even coverage and flawless finishes.'
    },
    {
      step: 4,
      title: 'Quality Inspection',
      description: 'Detailed final inspection and walkthrough. We ensure complete satisfaction and address any concerns before project completion.'
    }
  ];

  const testimonials = [
    {
      name: 'Robert Williams',
      property: 'Commercial Office',
      rating: 5,
      text: 'Excellent service for our Bellville office building. They worked efficiently during after-hours, minimizing disruption to our business. Professional team and outstanding results.',
      image: 'https://public.readdy.ai/ai/img_res/e0b065e3ef165c60a0ea4edd6e798cc9.jpg'
    },
    {
      name: 'Maria Jacobs',
      property: 'Family Home',
      rating: 5,
      text: 'Outstanding work on our Bellville home. The painters were respectful, clean, and delivered beautiful results. Highly recommend for quality painting services at fair prices.',
      image: 'https://readdy.ai/api/search-image?query=Elegant%20woman%20homeowner%20at%20Bellville%20family%20home%2C%20professionally%20painted%20interior%2C%20satisfied%20client%20portrait%2C%20quality%20residential%20property&width=80&height=80&seq=bellville2&orientation=squarish'
    },
    {
      name: 'David Kruger',
      property: 'Retail Store',
      rating: 5,
      text: 'Professional service for our retail space. They completed the work quickly without disrupting our business operations. Great value and highly professional throughout the project.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20retail%20owner%20at%20Bellville%20shopping%20center%2C%20newly%20painted%20store%2C%20satisfied%20customer%2C%20quality%20commercial%20painting&width=80&height=80&seq=bellville3&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Commercial Office Building',
      description: 'Complete exterior and interior painting for a Bellville office complex. Modern color scheme with efficient completion during after-hours.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20commercial%20office%20building%20in%20Bellville%20Northern%20Suburbs%2C%20freshly%20painted%20exterior%2C%20business%20property%2C%20professional%20painting%20work%2C%20quality%20finish&width=400&height=300&seq=bellville4&orientation=landscape'
    },
    {
      title: 'Residential Estate',
      description: 'Multi-unit residential project with coordinated color scheme. Quality finishes throughout this Bellville residential development.',
      image: 'https://readdy.ai/api/search-image?query=Bellville%20residential%20estate%2C%20newly%20painted%20homes%2C%20Northern%20Suburbs%20property%2C%20professional%20painting%20finish%2C%20quality%20workmanship&width=400&height=300&seq=bellville5&orientation=landscape'
    },
    {
      title: 'Retail Shopping Center',
      description: 'Large-scale retail project with coordinated exterior scheme. Completed efficiently with minimal disruption to business operations.',
      image: 'https://readdy.ai/api/search-image?query=Bellville%20shopping%20center%2C%20professional%20exterior%20painting%2C%20retail%20property%2C%20modern%20commercial%20development%2C%20quality%20finish%20work&width=400&height=300&seq=bellville6&orientation=landscape'
    },
    {
      title: 'Industrial Facility',
      description: 'Complete industrial painting project with protective coatings. Enhanced durability and professional appearance for this Bellville facility.',
      image: 'https://readdy.ai/api/search-image?query=Bellville%20industrial%20facility%2C%20professionally%20painted%20exterior%2C%20commercial%20property%2C%20industrial%20painting%20work%2C%20Northern%20Suburbs%20quality%20finish&width=400&height=300&seq=bellville7&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20commercial%20property%20in%20Bellville%20Northern%20Suburbs%2C%20painting%20equipment%2C%20scaffolding%2C%20business%20district%2C%20sunny%20day%2C%20Cape%20Town%20area&width=1920&height=1080&seq=bellville_hero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Bellville
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Quality painting services for Bellville homes and businesses. Expert craftsmanship for residential and commercial properties in the Northern Suburbs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Bellville Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Trusted Painting Services in Bellville
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Bellville is a major commercial and residential hub in Cape Town's Northern Suburbs, known for its thriving business district, shopping centers, and diverse residential areas. This dynamic area combines commercial vitality with established neighborhoods.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Our painting team has extensive experience serving Bellville's diverse property market, from commercial offices and retail spaces to residential homes. We understand the unique requirements of both business and residential clients in this busy area.
              </p>
              <p className="text-lg text-gray-600">
                Whether you need commercial painting with flexible scheduling or residential services for your Bellville home, we deliver professional results that enhance your property and meet your specific needs.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Bellville%20commercial%20and%20residential%20area%20Northern%20Suburbs%2C%20business%20district%2C%20shopping%20centers%2C%20diverse%20Cape%20Town%20suburb%2C%20urban%20development&width=600&height=400&seq=bellville8&orientation=landscape"
                alt="Bellville Properties"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bellville Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive painting solutions for residential and commercial properties throughout Bellville and the Northern Suburbs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="text-sm text-gray-600 space-y-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center">
                      <i className="ri-check-line text-yellow-600 mr-2"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Bellville Painters
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional service, quality materials, and expert craftsmanship for every Bellville painting project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring quality results and complete satisfaction for every Bellville project.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold" style={{backgroundColor: '#ffff00'}}>
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Bellville Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our recent painting projects across Bellville residential and commercial properties.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Testimonials from satisfied Bellville property owners who have experienced our professional painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Transform Your Bellville Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Bellville's trusted painting professionals. Get a free quote for your residential or commercial property.
          </p>
          <Link
            to="/contact"
            className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            Get Free Quote
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
